import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, TrendingUp, Award, Calendar, FileText, Image as ImageIcon, Paperclip } from 'lucide-react';
import { getChildTheme } from '../childThemes';
import api from '../services/api';

const ChildGrades = () => {
  const [child, setChild] = useState(null);
  const [theme, setTheme] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [expandedSubject, setExpandedSubject] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [completedWork, setCompletedWork] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const childUser = localStorage.getItem('childUser');
    const childToken = localStorage.getItem('childToken');
    
    if (!childUser || !childToken) {
      navigate('/child/login');
      return;
    }
    
    // Set API token from childToken for authenticated requests
    api.setToken(childToken);
    
    const childData = JSON.parse(childUser);
    setChild(childData);
    const childTheme = getChildTheme(childData.theme || 'purple');
    setTheme(childTheme);
    
    // Load photo from localStorage immediately for instant display
    const cachedPhotoUrl = localStorage.getItem(`child_photo_${childData.id}`);
    if (cachedPhotoUrl && cachedPhotoUrl !== 'null') {
      setProfilePhoto(cachedPhotoUrl);
    }
    
    loadGrades(childData.id);
  }, [navigate]);

  const loadGrades = async (childId) => {
    try {
      const [workData, subjectsData] = await Promise.all([
        api.getCompletedWork(childId),
        api.getSubjects()
      ]);
      setCompletedWork(workData);
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Failed to load grades:', error);
    }
  };

  if (!child || !theme) return null;

  // Determine semesters based on completed work dates
  const getSemester = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Unknown';
    
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // 0-indexed
    
    if (month >= 8 && month <= 12) {
      return `Fall ${year}`;
    } else if (month >= 1 && month <= 5) {
      return `Spring ${year}`;
    } else {
      return `Summer ${year}`;
    }
  };

  // Get unique semesters from completed work
  const semesters = [...new Set(
    completedWork.map(w => getSemester(w.completed_date || w.due_date || w.date_assigned))
  )].filter(s => s !== 'Unknown');
  semesters.sort().reverse(); // Most recent first

  console.log('Completed work:', completedWork);
  console.log('Detected semesters:', semesters);

  // Filter completed work by selected semester
  const filteredWork = selectedSemester === 'all' 
    ? completedWork 
    : completedWork.filter(w => getSemester(w.completed_date || w.due_date || w.date_assigned) === selectedSemester);

  // Get unique subjects from filtered completed work
  // Map each work item to its subject, then deduplicate by subject ID
  const subjectMap = new Map();
  filteredWork.forEach(w => {
    const subject = subjects.find(s => 
      String(s.id) === String(w.subject_id) || 
      (s.replaces_default_id && String(s.replaces_default_id) === String(w.subject_id))
    );
    if (subject && !subjectMap.has(subject.id)) {
      subjectMap.set(subject.id, subject);
    }
  });
  const childSubjects = Array.from(subjectMap.values());

  // Calculate grades per subject
  const subjectGrades = childSubjects.map(subject => {
    const subjectWork = filteredWork.filter(w => 
      String(w.subject_id) === String(subject.id) || 
      (subject.replaces_default_id && String(w.subject_id) === String(subject.replaces_default_id))
    );

    // Convert letter grades or points to percentages for averaging
    const gradeToPercentage = (work) => {
      // If points are available, calculate from points
      if (work.points_awarded !== undefined && work.points_possible !== undefined && work.points_possible > 0) {
        return (work.points_awarded / work.points_possible) * 100;
      }
      
      // Otherwise, fall back to letter grade mapping
      const gradeMap = {
        'A+': 98, 'A': 95, 'A-': 92,
        'B+': 88, 'B': 85, 'B-': 82,
        'C+': 78, 'C': 75, 'C-': 72,
        'D+': 68, 'D': 65, 'D-': 62,
        'F': 50
      };
      return gradeMap[work.letter_grade] || 0;
    };

    const percentageToGrade = (percent) => {
      if (percent >= 97) return 'A+';
      if (percent >= 93) return 'A';
      if (percent >= 90) return 'A-';
      if (percent >= 87) return 'B+';
      if (percent >= 83) return 'B';
      if (percent >= 80) return 'B-';
      if (percent >= 77) return 'C+';
      if (percent >= 73) return 'C';
      if (percent >= 70) return 'C-';
      if (percent >= 67) return 'D+';
      if (percent >= 63) return 'D';
      if (percent >= 60) return 'D-';
      return 'F';
    };

    // Calculate current average using weighted points (not average of percentages)
    const totalPoints = subjectWork.reduce((sum, w) => {
      return sum + (w.points_possible || 0);
    }, 0);
    
    const earnedPoints = subjectWork.reduce((sum, w) => {
      return sum + (w.points_awarded || 0);
    }, 0);
    
    const currentAverage = totalPoints > 0
      ? (earnedPoints / totalPoints) * 100
      : 0;

    const currentGrade = percentageToGrade(currentAverage);

    // For semester grade (could be same as current for now, or weighted differently)
    const semesterGrade = currentGrade;
    const semesterPercentage = currentAverage;

    return {
      subject,
      subjectName: subject.name,
      assignments: subjectWork,
      currentGrade,
      currentPercentage: Math.round(currentAverage),
      semesterGrade,
      semesterPercentage: Math.round(semesterPercentage),
      assignmentCount: subjectWork.length,
      totalPoints,
      earnedPoints,
      trend: currentAverage >= 90 ? 'excellent' : currentAverage >= 80 ? 'good' : currentAverage >= 70 ? 'average' : 'needs-work'
    };
  }).filter(sg => sg.assignmentCount > 0); // Only show subjects with grades

  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return 'bg-green-100 text-green-700 border-green-300';
    if (grade.startsWith('B')) return 'bg-blue-100 text-blue-700 border-blue-300';
    if (grade.startsWith('C')) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    if (grade.startsWith('D')) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };

  const getTrendEmoji = (trend) => {
    switch (trend) {
      case 'excellent': return '🌟';
      case 'good': return '👍';
      case 'average': return '📊';
      case 'needs-work': return '📚';
      default: return '📈';
    }
  };

  const handleAssignmentClick = (assignment) => {
    setSelectedAssignment(assignment);
    setShowAssignmentDialog(true);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.cardBg}`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${theme.headerBg} text-white p-6 shadow-lg`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/child/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            {/* Profile Photo */}
            <div
              className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-xl font-bold shadow-lg overflow-hidden cursor-pointer"
              style={{ color: child.profileColor }}
              onClick={() => navigate('/child/dashboard')}
            >
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                child.name.charAt(0)
              )}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">My Grades</h1>
              <p className={`text-${theme.accentColor === 'purple-600' ? 'purple' : 'blue'}-100`}>
                Track your progress in every subject
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Semester Selector - only show if multiple semesters */}
              {semesters.length > 1 && (
                <Select value={selectedSemester} onValueChange={setSelectedSemester}>
                  <SelectTrigger className="w-48 bg-white/20 backdrop-blur-sm border-white/30 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Semesters</SelectItem>
                    {semesters.map(semester => (
                      <SelectItem key={semester} value={semester}>
                        {semester}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {subjectGrades.length === 0 ? (
          <Card className="p-12 text-center">
            <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-2xl text-slate-500 mb-2">No grades yet!</p>
            <p className="text-slate-400">Complete assignments to see your grades here.</p>
          </Card>
        ) : (
          <>
            {/* Subject Grade Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {subjectGrades.map((subjectGrade, index) => {
                const colors = [
                  'from-blue-500 to-blue-600',
                  'from-green-500 to-green-600',
                  'from-purple-500 to-purple-600',
                  'from-orange-500 to-orange-600',
                  'from-pink-500 to-pink-600',
                  'from-teal-500 to-teal-600',
                  'from-indigo-500 to-indigo-600',
                  'from-red-500 to-red-600',
                ];
                const colorClass = colors[index % colors.length];
                const isSelected = expandedSubject === subjectGrade.subjectName;

                return (
                  <Card 
                    key={subjectGrade.subjectName}
                    onClick={() => setExpandedSubject(isSelected ? null : subjectGrade.subjectName)}
                    className={`p-6 border-0 shadow-lg bg-gradient-to-br ${colorClass} text-white cursor-pointer transform transition-all duration-200 ${isSelected ? 'scale-105 ring-4 ring-white' : 'hover:scale-105'}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-3 bg-white/20 rounded-lg text-2xl">
                        {subjectGrade.subject?.icon || '📚'}
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium opacity-90 block">{subjectGrade.subjectName}</span>
                        <span className="text-xs opacity-75">{subjectGrade.assignmentCount} assignments</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-4xl font-bold">{subjectGrade.currentPercentage}%</p>
                      <p className="text-4xl font-bold">{subjectGrade.currentGrade}</p>
                    </div>
                    <p className="text-xs opacity-75 mt-1">{getTrendEmoji(subjectGrade.trend)} {subjectGrade.trend.replace('-', ' ')}</p>
                  </Card>
                );
              })}
            </div>

            {/* Assignment List for Selected Subject */}
            {expandedSubject && (
              <Card className="overflow-hidden border-0 shadow-lg bg-white mb-8">
                {(() => {
                  const subjectGrade = subjectGrades.find(sg => sg.subjectName === expandedSubject);
                  if (!subjectGrade) return null;

                  return (
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                          <div 
                            className="p-3 rounded-lg text-3xl"
                            style={{ backgroundColor: `${subjectGrade.subject?.color}20` }}
                          >
                            {subjectGrade.subject?.icon || '📚'}
                          </div>
                          <div>
                            <h2 className="text-3xl font-bold text-slate-900">
                              {subjectGrade.subjectName}
                            </h2>
                            <p className="text-slate-600">{subjectGrade.assignmentCount} assignment{subjectGrade.assignmentCount !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={`text-2xl px-6 py-3 flex items-center justify-between gap-4 ${getGradeColor(subjectGrade.currentGrade)}`}>
                            <span>{subjectGrade.currentPercentage}%</span>
                            <span>{subjectGrade.currentGrade}</span>
                          </Badge>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                          <span>Overall Progress</span>
                          <span>{subjectGrade.currentPercentage}%</span>
                        </div>
                        <Progress value={parseFloat(subjectGrade.currentPercentage)} className="h-3" />
                      </div>

                      {/* Assignment List */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-slate-900 text-sm uppercase tracking-wide">Assignments</h4>
                        {subjectGrade.assignments.map((assignment, idx) => (
                          <div
                            key={assignment.id}
                            className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                            onClick={() => handleAssignmentClick(assignment)}
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center font-bold text-slate-700">
                                {idx + 1}
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900 flex items-center gap-2">
                                  {assignment.title}
                                  {assignment.attachments && assignment.attachments.length > 0 && (
                                    <Paperclip className="w-4 h-4 text-blue-600" />
                                  )}
                                </p>
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(assignment.completed_date || assignment.completedDate).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {assignment.notes && (
                                <p className="text-sm text-slate-600 max-w-xs italic">"{assignment.notes}"</p>
                              )}
                              <Badge className={`${getGradeColor(assignment.letter_grade)} text-xl px-4 py-2`}>
                                {assignment.points_awarded && assignment.points_possible 
                                  ? `${assignment.points_awarded}/${assignment.points_possible}`
                                  : assignment.letter_grade}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-6 p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-slate-700">Progress to A+</span>
                          <span className="text-sm font-semibold text-blue-700">
                            {Math.min(100, (parseFloat(subjectGrade.currentPercentage) / 97) * 100).toFixed(0)}%
                          </span>
                        </div>
                        <Progress
                          value={Math.min(100, (parseFloat(subjectGrade.currentPercentage) / 97) * 100)}
                          className="h-3"
                        />
                      </div>
                    </div>
                  );
                })()}
              </Card>
            )}
          </>
        )}

        {/* Grade Legend */}
        <Card className="p-6 mt-8 bg-gradient-to-br from-blue-50 to-purple-50 border-0">
          <h3 className="font-bold text-slate-900 mb-4">Grade Scale</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-bold mb-1">A</div>
              <p className="text-xs text-slate-600">90-100%</p>
            </div>
            <div className="text-center">
              <div className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-bold mb-1">B</div>
              <p className="text-xs text-slate-600">80-89%</p>
            </div>
            <div className="text-center">
              <div className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg font-bold mb-1">C</div>
              <p className="text-xs text-slate-600">70-79%</p>
            </div>
            <div className="text-center">
              <div className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg font-bold mb-1">D</div>
              <p className="text-xs text-slate-600">60-69%</p>
            </div>
            <div className="text-center">
              <div className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-bold mb-1">F</div>
              <p className="text-xs text-slate-600">Below 60%</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Assignment Detail Dialog */}
      <Dialog open={showAssignmentDialog} onOpenChange={setShowAssignmentDialog}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedAssignment && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-slate-900">
                  {selectedAssignment.title}
                </DialogTitle>
              </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Grade and Date */}
              <div className="flex items-center gap-4 pb-4 border-b">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Grade</p>
                  <Badge className={`${getGradeColor(selectedAssignment.letter_grade)} text-2xl px-6 py-3`}>
                    {selectedAssignment.points_awarded && selectedAssignment.points_possible 
                      ? `${selectedAssignment.points_awarded}/${selectedAssignment.points_possible} (${Math.round((selectedAssignment.points_awarded / selectedAssignment.points_possible) * 100)}%)`
                      : selectedAssignment.letter_grade}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Completed</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {new Date(selectedAssignment.completedDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              {/* Description */}
              {selectedAssignment.description && (
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">Description</h3>
                  <p className="text-slate-700">{selectedAssignment.description}</p>
                </div>
              )}

              {/* Teacher's Notes */}
              {selectedAssignment.notes && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                    📝 Teacher's Feedback
                  </h3>
                  <p className="text-blue-900">{selectedAssignment.notes}</p>
                </div>
              )}

              {/* Attachments/Submitted Work */}
              {selectedAssignment.attachments && selectedAssignment.attachments.length > 0 && (
                <div>
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <Paperclip className="w-5 h-5" />
                    Submitted Work ({selectedAssignment.attachments.length})
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedAssignment.attachments.map((attachment, idx) => (
                      <div
                        key={idx}
                        className="p-4 border-2 border-slate-200 rounded-lg hover:border-blue-400 transition-colors cursor-pointer bg-slate-50"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          {attachment.type === 'image' ? (
                            <ImageIcon className="w-8 h-8 text-blue-600" />
                          ) : (
                            <FileText className="w-8 h-8 text-slate-600" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-900 truncate">{attachment.name}</p>
                            <p className="text-xs text-slate-500 uppercase">{attachment.type}</p>
                          </div>
                        </div>
                        {attachment.type === 'image' && (
                          <div className="mt-2 bg-slate-200 rounded h-32 flex items-center justify-center">
                            <p className="text-slate-500 text-sm">Image Preview</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-3 italic">
                    💡 In a real application, you would be able to view and download these files
                  </p>
                </div>
              )}

              {!selectedAssignment.attachments || selectedAssignment.attachments.length === 0 && (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">No attachments for this assignment</p>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
    </div>
  );
};

export default ChildGrades;
